import classNames from 'classnames';
import { Business } from 'model';
import { useAddBusinessBannerMutation, useAddBusinessLogoMutation } from '../api/BusinessApi';
import GenericBusinessIcon from '../assets/buildingIcon.png';
import { FieldValuesType, FormHookReturnType } from '../hooks/useFormValues';
import { useLoading } from '../hooks/useQuirkLoading';
import { getBusinessImages, isGenericImage } from '../util/getBusinessImages';
import styles from './EditBusinessImage.module.css';
import { ImageUploadInput } from './Forms/ImageUploadInput';
import { SimpleCSSColor, SimpleCSSColorInput } from './Forms/SimpleCSSColorInput';
import { Header } from './Header';
import { IconButton } from './IconButton';
import { ImagePreview, ImagePreviewType } from './ImagePreview';
import { IconArrowBack, IconTrash } from './QuirkIcons';
import { QuirkIonContent } from './QuirkIonContent';
import { WebsiteImages } from './WebsiteImages';

export interface EditBusinessImageProps {
	onDoneClick: () => void | Promise<void>;
	form: FormHookReturnType<Business>;
	image?: ImagePreviewType;
}

export const EditBusinessImage: React.FC<EditBusinessImageProps> = ({
	onDoneClick,
	form,
	image = 'cover'
}: EditBusinessImageProps) => {
	const { setValues, formValues, stateDataObject: business } = form;
	const isLoading = formValues === undefined || business === undefined || business === null;
	useLoading({ isLoading });
	const isLogo = image === 'logo';
	const { logo, banner } = business ? getBusinessImages(business) : { logo: '', banner: '' };
	const bgColor: SimpleCSSColor = isLogo
		? ((formValues?.logoBackgroundColor?.value ?? 'transparent') as SimpleCSSColor)
		: 'transparent';
	const showRemove = !isGenericImage(image === 'cover' ? banner : logo);
	const key = image === 'cover' ? 'banner' : 'logo';
	return (
		<div className={styles.EditBusinessImage}>
			<Header title={`Edit ${image}`} left={<IconButton icon={<IconArrowBack />} onClick={onDoneClick} />} />
			<QuirkIonContent>
				<>
					{!isLoading && (
						<>
							<div className={classNames(styles.formRow, styles.removeWrapper)}>
								<ImageUploadInput
									fieldName={isLogo ? 'logo' : 'banner'}
									document={business}
									setValues={setValues}
									label="Upload new"
									addImage={isLogo ? useAddBusinessLogoMutation : useAddBusinessBannerMutation}
								/>
							</div>
							<ImagePreview
								type={image}
								image={isLogo ? logo || GenericBusinessIcon : banner}
								bgColor={bgColor}
							/>
							<div className={styles.innerWrapper}>
								{showRemove && (
									<div className={classNames(styles.formRow, styles.removeWrapper)}>
										<IconButton
											iconRight
											icon={<IconTrash />}
											label="Remove image"
											onClick={() => {
												setValues({ [key]: '', [`${key}URL`]: '' });
											}}
											outline
										/>
									</div>
								)}
								{image === 'logo' && (
									<div className={classNames(styles.formRow, styles.end)}>
										<SimpleCSSColorInput
											id="logoBackgroundColor"
											label="Background color"
											colors={['#ffffff', '#000000', 'transparent']}
											setValues={setValues}
											field={formValues?.logoBackgroundColor as FieldValuesType<SimpleCSSColor>}
										/>
									</div>
								)}
								<div className={styles.formRow}>
									<WebsiteImages
										business={business}
										value={(image === 'logo' ? logo : banner) ?? ''}
										onChange={url => {
											setValues({ [`${key}URL`]: url, [key]: '' });
										}}
										onChangeWebsite={website => {
											setValues({ website });
										}}
										type={image}
									/>
								</div>
							</div>
						</>
					)}
				</>
			</QuirkIonContent>
		</div>
	);
};
